import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from './ui/Dialog';
import { AlertCircle, CheckCircle, AlertTriangle, Info, TrendingUp, Activity, HardDrive, Network } from 'lucide-react';
import { Skeleton } from './ui/Skeleton';
import { apiClient, type ManagedClusterKind } from '../lib/api-client';
import { Button } from './ui/Button';

interface ClusterStatusReportProps {
  tenantCode: string;
  instanceName: string;
  cloudProvider: string;
  clusterKind?: ManagedClusterKind;
}

interface NodeInfo {
  name: string;
  status: string;
  roles: string[];
  cpuCapacity: string;
  memoryCapacity: string;
  pods: number;
}

interface PodStats {
  running: number;
  pending: number;
  failed: number;
  succeeded: number;
  total: number;
}

interface ResourceMetrics {
  cpu: {
    used: number;
    available: number;
    percent: number;
    unit: string;
  };
  memory: {
    used: number;
    available: number;
    percent: number;
    unit: string;
  };
  filesystem: {
    used: number;
    available: number;
    percent: number;
    unit: string;
  };
  network: {
    received: number;
    transmitted: number;
    unit: string;
  };
}

interface ClusterIssue {
  title: string;
  risk: 'critical' | 'important' | 'moderate' | 'low';
  description: string;
  affectedResources: string[];
}

interface FailedPod {
  namespace: string;
  name: string;
  reason: string;
  message: string;
  startTime: string;
}

interface PendingPod {
  namespace: string;
  name: string;
  reason: string;
  message: string;
  startTime: string;
}

interface ClusterStatus {
  version: string;
  nodes: NodeInfo[];
  podStats: PodStats;
  pvcs: number;
  issues: ClusterIssue[];
  metrics: ResourceMetrics;
  failedPods?: FailedPod[];
  pendingPods?: PendingPod[];
}

export function ClusterStatusReport({ tenantCode, instanceName, cloudProvider, clusterKind }: ClusterStatusReportProps) {
  const [status, setStatus] = useState<ClusterStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liveMetrics, setLiveMetrics] = useState<ResourceMetrics | null>(null);
  const [selectedRisk, setSelectedRisk] = useState<string | null>(null);
  const [showIssuesDialog, setShowIssuesDialog] = useState(false);
  const [showFailedPodsDialog, setShowFailedPodsDialog] = useState(false);
  const [showPendingPodsDialog, setShowPendingPodsDialog] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiClient.getClusterStatus(tenantCode, instanceName, cloudProvider, clusterKind);
        setStatus(data);
        setLiveMetrics(data.metrics);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch cluster status');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();

    // Set up live metrics streaming (every 5 seconds)
    const metricsInterval = setInterval(async () => {
      try {
        const data = await apiClient.getClusterMetrics(tenantCode, instanceName, cloudProvider, clusterKind);
        setLiveMetrics(data);
      } catch (err) {
        console.error('Failed to fetch live metrics:', err);
      }
    }, 5000);

    return () => clearInterval(metricsInterval);
  }, [tenantCode, instanceName, cloudProvider, clusterKind]);

  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case 'critical':
        return <Badge variant="destructive" className="bg-red-600 text-white">Critical</Badge>;
      case 'important':
        return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">Important</Badge>;
      case 'moderate':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">Moderate</Badge>;
      case 'low':
        return <Badge variant="outline">Low</Badge>;
      default:
        return <Badge variant="outline">{risk}</Badge>;
    }
  };

  const getRiskIcon = (risk: string) => {
    switch (risk) {
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'important':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'moderate':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'low':
        return <Info className="w-5 h-5 text-blue-600" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getUtilizationPercentage = (used: number, available: number) => {
    if (available === 0) return 0;
    return Math.round((used / available) * 100);
  };

  const getUtilizationColor = (percentage: number | null | undefined) => {
    const percent = percentage ?? 0;
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 75) return 'bg-orange-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const safeToFixed = (value: number | null | undefined, decimals: number): string => {
    if (value === null || value === undefined || !isFinite(value)) return '0';
    return value.toFixed(decimals);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-20 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400 font-semibold">Status report unavailable</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Couldn’t derive the report right now. Try again later.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!status) return null;

  const currentMetrics = liveMetrics || status.metrics;

  return (
    <div className="space-y-4">
      {/* Cluster Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Cluster Information</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Version</p>
              <p className="text-base font-semibold">{status.version}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Nodes</p>
              <p className="text-base font-semibold">{status.nodes.length}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Persistent Volume Claims</p>
              <p className="text-base font-semibold">{status.pvcs}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Issues by Risk */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Issues by Risk</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {['critical', 'important', 'moderate', 'low'].map(risk => {
              const count = status.issues.filter(issue => issue.risk === risk).length;
              
              // Color coding for each risk level
              const riskColors: Record<string, string> = {
                critical: 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
                important: 'text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20',
                moderate: 'text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20',
                low: 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20',
              };
              
              return (
                <button
                  key={risk}
                  onClick={() => {
                    setSelectedRisk(risk);
                    setShowIssuesDialog(true);
                  }}
                  className={`text-center p-2 rounded-lg transition-colors cursor-pointer ${riskColors[risk]} ${count === 0 ? 'opacity-50' : ''}`}
                >
                  <div className={`text-xl font-bold ${count === 0 ? 'text-gray-400' : ''}`}>{count}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 capitalize">{risk}</div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Live Cluster Utilization */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Cluster Utilization (Live)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* CPU */}
            <div>
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  CPU
                </span>
                <div className="flex flex-col items-end gap-0.5 text-right">
                  <span className="text-lg font-bold text-blue-600">{safeToFixed(currentMetrics.cpu.percent, 1)}%</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {safeToFixed(currentMetrics.cpu.used, 2)} / {safeToFixed(currentMetrics.cpu.available, 2)} {currentMetrics.cpu.unit}
                  </span>
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${getUtilizationColor(currentMetrics.cpu.percent)}`}
                  style={{ width: `${Math.min(currentMetrics.cpu.percent ?? 0, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Memory */}
            <div>
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-sm font-medium flex items-center gap-2">
                  <HardDrive className="w-4 h-4" />
                  Memory
                </span>
                <div className="flex flex-col items-end gap-0.5 text-right">
                  <span className="text-lg font-bold text-blue-600">{safeToFixed(currentMetrics.memory.percent, 1)}%</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {safeToFixed(currentMetrics.memory.used, 2)} / {safeToFixed(currentMetrics.memory.available, 2)} {currentMetrics.memory.unit}
                  </span>
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${getUtilizationColor(currentMetrics.memory.percent)}`}
                  style={{ width: `${Math.min(currentMetrics.memory.percent ?? 0, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Filesystem */}
            <div>
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-sm font-medium flex items-center gap-2">
                  <HardDrive className="w-4 h-4" />
                  Filesystem
                </span>
                <div className="flex flex-col items-end gap-0.5 text-right">
                  {(currentMetrics.filesystem.used ?? 0) === 0 && (currentMetrics.filesystem.available ?? 0) === 0 ? (
                    <>
                      <span className="text-lg font-bold text-gray-500 dark:text-gray-400">Unavailable</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">—</span>
                    </>
                  ) : (
                    <>
                      <span className="text-lg font-bold text-blue-600">{safeToFixed(currentMetrics.filesystem.percent, 1)}%</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {safeToFixed(currentMetrics.filesystem.used, 2)} / {safeToFixed(currentMetrics.filesystem.available, 2)} {currentMetrics.filesystem.unit}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${getUtilizationColor(currentMetrics.filesystem.percent)}`}
                  style={{ width: `${Math.min(currentMetrics.filesystem.percent ?? 0, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pod Statistics */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Pod Statistics</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <div className="text-center">
              <div className="text-xl font-bold text-green-600">{status.podStats.running}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Running</div>
            </div>
            <button
              onClick={() => setShowPendingPodsDialog(true)}
              className="text-center p-2 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors cursor-pointer"
              disabled={status.podStats.pending === 0}
            >
              <div className={`text-xl font-bold ${status.podStats.pending > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>{status.podStats.pending}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Pending</div>
            </button>
            <button 
              onClick={() => setShowFailedPodsDialog(true)}
              className="text-center p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
              disabled={status.podStats.failed === 0}
            >
              <div className={`text-xl font-bold ${status.podStats.failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>{status.podStats.failed}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Failed</div>
            </button>
            <div className="text-center">
              <div className="text-xl font-bold text-blue-600">{status.podStats.succeeded}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Succeeded</div>
            </div>
            <div className="text-center col-span-2 sm:col-span-1">
              <div className="text-xl font-bold">{status.podStats.total}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Total</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Node List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Nodes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {status.nodes.map((node) => (
              <div key={node.name} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <h4 className="font-semibold break-all sm:break-normal min-w-0">{node.name}</h4>
                    {node.status === 'Ready' ? (
                      <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Ready</Badge>
                    ) : (
                      <Badge variant="destructive">{node.status}</Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 sm:gap-2 sm:justify-end">
                    {node.roles.map((role) => (
                      <Badge key={role} variant="outline" className="text-xs">{role}</Badge>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center justify-between sm:block">
                    <span className="block text-xs">CPU Capacity</span>
                    <span className="font-medium">{node.cpuCapacity}</span>
                  </div>
                  <div className="flex items-center justify-between sm:block">
                    <span className="block text-xs">Memory Capacity</span>
                    <span className="font-medium">{node.memoryCapacity}</span>
                  </div>
                  <div className="flex items-center justify-between sm:block">
                    <span className="block text-xs">Pods</span>
                    <span className="font-medium">{node.pods}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Issues Detail Dialog */}
      {showIssuesDialog && selectedRisk && (
        <Dialog open={showIssuesDialog} onOpenChange={setShowIssuesDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedRisk.charAt(0).toUpperCase() + selectedRisk.slice(1)} Risk Issues</DialogTitle>
            </DialogHeader>
            <DialogBody className="max-h-[60vh]">
              {status.issues.filter(issue => issue.risk === selectedRisk).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No {selectedRisk} risk issues found
                </div>
              ) : (
                <div className="space-y-3">
                  {status.issues.filter(issue => issue.risk === selectedRisk).map((issue, idx) => (
                    <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-sm">{issue.title}</h4>
                        <Badge 
                          variant={
                            issue.risk === 'critical' ? 'destructive' : 
                            issue.risk === 'important' ? 'destructive' :
                            issue.risk === 'moderate' ? 'default' : 
                            'outline'
                          }
                          className="ml-2 capitalize"
                        >
                          {issue.risk}
                        </Badge>
                      </div>
                      {issue.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">{issue.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setShowIssuesDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Failed Pods Dialog */}
      {showFailedPodsDialog && (
        <Dialog open={showFailedPodsDialog} onOpenChange={setShowFailedPodsDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Failed Pods ({status.failedPods?.length || 0})</DialogTitle>
            </DialogHeader>
            <DialogBody className="max-h-[60vh]">
              {(!status.failedPods || status.failedPods.length === 0) ? (
                <div className="text-center py-8 text-gray-500">
                  No failed pods found
                </div>
              ) : (
                <div className="space-y-3">
                  {status.failedPods.map((pod, idx) => (
                    <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-sm">{pod.name}</h4>
                          <p className="text-xs text-gray-500">{pod.namespace}</p>
                        </div>
                        <Badge variant="destructive" className="ml-2">
                          {pod.reason}
                        </Badge>
                      </div>
                      {pod.startTime && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                          Started: {new Date(pod.startTime).toLocaleString()}
                        </p>
                      )}
                      {pod.message && (
                        <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300 font-mono overflow-x-auto">
                          {pod.message.split('\n').slice(-10).join('\n')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setShowFailedPodsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Pending Pods Dialog */}
      {showPendingPodsDialog && (
        <Dialog open={showPendingPodsDialog} onOpenChange={setShowPendingPodsDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Pending Pods ({status.pendingPods?.length || 0})</DialogTitle>
            </DialogHeader>
            <DialogBody className="max-h-[60vh]">
              {(!status.pendingPods || status.pendingPods.length === 0) ? (
                <div className="text-center py-8 text-gray-500">
                  No pending pods found
                </div>
              ) : (
                <div className="space-y-3">
                  {status.pendingPods.map((pod, idx) => (
                    <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-sm">{pod.name}</h4>
                          <p className="text-xs text-gray-500">{pod.namespace}</p>
                        </div>
                        <Badge className="ml-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                          {pod.reason || 'Pending'}
                        </Badge>
                      </div>
                      {pod.startTime && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                          Started: {new Date(pod.startTime).toLocaleString()}
                        </p>
                      )}
                      {pod.message && (
                        <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300 font-mono overflow-x-auto">
                          {pod.message.split('\n').slice(-10).join('\n')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setShowPendingPodsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

